import React, {useState} from "react";
import {downloadFile} from "./helpers/downloadFile";
import {deleteItem} from "./helpers/deleteItem";
import {FiDownload, FiAlertCircle, FiTrash2} from "react-icons/fi";
import {AiOutlineDelete, AiFillEye} from "react-icons/ai";
import {
  Box,
  Icon,
  Badge,
  Flex,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  useDisclosure,
  VStack,
  HStack,
  Center,
} from "@chakra-ui/react";
import PreviewFile from "./PreviewFile";
import {previewFile} from "./helpers/previewFile";

export default function PatientSelectedControls({
  selectedFile,
  path,
  setFiles,
  setSelectedFile,
  preview,
  setPreview,
  toast,
  dashState,
  onDeleteSuccess,
}) {
  // Only allow delete for files in Client Uploads folder
  const canDelete = path.includes('/Client Uploads');
  
  // Delete confirmation modal
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [isDeleting, setIsDeleting] = useState(false);
  
  const handleDelete = async () => {
    setIsDeleting(true);
    await deleteItem(
      dashState,
      path,
      selectedFile,
      setFiles,
      setSelectedFile,
      toast,
      onDeleteSuccess
    );
    setIsDeleting(false);
    onDeleteClose();
  };
  
  return (
    <Box mr="1">
      <PreviewFile
        preview={preview}
        setPreview={setPreview}
        state={dashState}
        path={path}
      />
      <Flex gap={2} alignContent={"space-around"} align={"center"} flexWrap="wrap">
        {selectedFile && selectedFile?.slice(-1) !== "/" && (
          <>
            <Button
              size="sm"
              variant="outline"
              borderColor="rgba(0,0,0,0.08)"
              borderRadius="8px"
              fontWeight="500"
              fontSize="12px"
              color="gray.700"
              leftIcon={<Icon as={AiFillEye} fontSize="14px" />}
              onClick={() =>
                previewFile(dashState, path, selectedFile, setPreview, toast)
              }
              _hover={{bg: "rgba(0,0,0,0.02)", borderColor: "rgba(0,0,0,0.15)"}}
            >
              Preview
            </Button>
            <Button
              size="sm"
              variant="outline"
              borderColor="rgba(0,0,0,0.08)"
              borderRadius="8px"
              fontWeight="500"
              fontSize="12px"
              color="gray.700"
              leftIcon={<Icon as={FiDownload} fontSize="14px" />}
              onClick={() => downloadFile(dashState, path, selectedFile, toast)}
              _hover={{bg: "rgba(0,0,0,0.02)", borderColor: "rgba(0,0,0,0.15)"}}
            >
              Download
            </Button>
            {canDelete && (
              <Button
                size="sm"
                variant="outline"
                borderColor="rgba(220,53,69,0.15)"
                borderRadius="8px"
                fontWeight="500"
                fontSize="12px"
                color="red.500"
                leftIcon={<Icon as={AiOutlineDelete} fontSize="14px" />}
                onClick={onDeleteOpen}
                _hover={{bg: "red.50", borderColor: "red.200"}}
              >
                Delete
              </Button>
            )}
          </>
        )}
      </Flex>
      
      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={isDeleteOpen} 
        onClose={onDeleteClose} 
        isCentered
        motionPreset="slideInBottom"
        size="md"
      >
        <ModalOverlay 
          bg="blackAlpha.300"
        />
        <ModalContent borderRadius="xl" overflow="hidden">
          <Box bg="red.50" px={6} py={4}>
            <HStack spacing={3}>
              <Center 
                w="40px" 
                h="40px" 
                bg="red.100" 
                borderRadius="full"
              >
                <Icon as={FiTrash2} color="red.500" fontSize="20px" />
              </Center>
              <Box>
                <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                  Delete File
                </Text>
                <Text fontSize="sm" color="gray.600">
                  This action is permanent and cannot be undone
                </Text>
              </Box>
            </HStack>
          </Box>
          
          <ModalBody py={6}>
            <VStack spacing={4} align="stretch">
              <Box 
                p={4} 
                bg="gray.50" 
                borderRadius="lg"
                border="1px solid"
                borderColor="gray.200"
              >
                <HStack spacing={2} mb={1}>
                  <Icon as={FiAlertCircle} color="orange.500" fontSize="16px" />
                  <Text fontSize="sm" fontWeight="medium" color="gray.700">
                    You're about to delete:
                  </Text>
                </HStack>
                <Text 
                  fontSize="sm" 
                  color="gray.900" 
                  fontWeight="semibold"
                  bg="white"
                  px={3}
                  py={2}
                  borderRadius="md"
                  mt={2}
                  border="1px solid"
                  borderColor="gray.200"
                  wordBreak="break-all"
                >
                  {selectedFile}
                </Text>
              </Box>
              
              <Text fontSize="sm" color="gray.600" px={1}>
                Once deleted, this file will be permanently removed from your storage 
                and cannot be recovered.
              </Text>
            </VStack>
          </ModalBody>
          
          <ModalFooter bg="gray.50" px={6} py={4}>
            <HStack spacing={3} w="full">
              <Button 
                variant="outline"
                onClick={onDeleteClose}
                flex={1}
                borderColor="gray.300"
                _hover={{
                  bg: "gray.100",
                }}
              >
                Cancel
              </Button>
              <Button 
                bg="red.500"
                color="white"
                onClick={handleDelete}
                isLoading={isDeleting}
                loadingText="Deleting..."
                flex={1}
                _hover={{
                  bg: "red.600",
                }}
                _active={{
                  bg: "red.700",
                }}
                leftIcon={!isDeleting ? <Icon as={FiTrash2} /> : undefined}
              >
                Delete File
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}